import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeFiltro,
  CampoDeSelecao,
  Cartao,
  CartaoIndicador,
  EstadoCarregando,
  EstadoVazio,
  GrupoDeFiltro,
  OpcaoDeFiltro,
  Tabela,
  ValorFiscal,
  type ColunaTabela,
  type FiltroMarcado,
} from '../ui';
import { ErroDaApi } from '../../paginas/ErroDaApi';
import { useExercicioVigente } from '../../servicos/exercicio';
import { DetalheDoRecorrente } from './DetalheDoRecorrente';
import {
  AJUDA_DA_ETAPA,
  COR_DO_SERVICO,
  ETAPAS,
  ETAPAS_PADRAO,
  ROTULO_DA_ETAPA,
  ROTULO_DO_SERVICO,
  SERVICOS,
  buscarVisaoGeral,
  periodoDoAtalho,
  periodoDoExercicio,
  type Etapa,
  type FiltroDaVisaoGeral,
  type OperacaoResumo,
  type Ranking,
  type Servico,
  type VisaoGeral,
} from '../../servicos/visaoGeral';

const ROTULO_DO_TIPO: Readonly<Record<string, string>> = {
  EXPORTACAO: 'Exportação',
  DIFERIDO: 'Diferido',
  OUTRO: 'Outro',
};

/** O status de cada serviço, em palavras, para a lista de operações. */
const ROTULO_DA_SITUACAO: Readonly<Record<string, string>> = {
  SEM_CONTRATO: 'Só cálculo',
  ABERTO: 'Contrato aberto',
  APROVACAO_ASSESSOR: 'Aprovação do assessor',
  APROVACAO_FORNECEDOR: 'Aprovação do fornecedor',
  EM_ASSINATURA: 'Em assinatura',
  FECHADO: 'Fechado',
  SIMULACAO: 'Simulação',
  FECHADA: 'Fechada',
  PROTOCOLADA: 'Protocolada',
  TRANSFERIDA: 'Transferida',
  PAGA: 'Paga',
  CANCELADA: 'Cancelada',
  ATIVO: 'Ativo',
  FINALIZADO: 'Finalizado',
  CANCELADO: 'Cancelado',
};

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

type Atalho = 'EXERCICIO' | 'ULTIMOS_12' | 'ESTE_MES' | 'PERSONALIZADO';

const ROTULO_DO_ATALHO: Readonly<Record<Atalho, string>> = {
  EXERCICIO: 'Exercício',
  ULTIMOS_12: 'Últimos 12 meses',
  ESTE_MES: 'Este mês',
  PERSONALIZADO: 'Personalizado',
};

/**
 * Os anos do campo "Exercício": do vigente do Recorrente (o seguinte, a partir
 * de julho — RN-620) seis para trás, o mesmo alcance que o servidor aceita.
 */
/** Os anos do campo, a partir do vigente. */
function anosDoExercicio(vigente: number): readonly number[] {
  return Array.from({ length: 6 }, (_, i) => vigente - i);
}

function filtroInicial(): FiltroDaVisaoGeral {
  return {
    ...periodoDoExercicio(new Date().getFullYear()),
    servicos: [...SERVICOS],
    etapas: [...ETAPAS_PADRAO],
    clienteId: '',
    fornecedorId: '',
    assessorId: '',
    tipoCredito: '',
  };
}

/**
 * A visão geral do Painel (D-015): os três serviços somados e separados.
 *
 * As perguntas que a tela responde, de cima para baixo: quanto foi negociado e
 * quanto os clientes economizaram; quanto entrou, quanto vai entrar e quanto
 * está atrasado; como isso se divide entre os serviços e ao longo dos meses; o
 * que ainda está no funil; e quem concentra o volume.
 */
export function PainelGeral(): JSX.Element {
  const vigente = useExercicioVigente();
  const [filtro, setFiltro] = useState<FiltroDaVisaoGeral>(filtroInicial);
  const [atalho, setAtalho] = useState<Atalho>('EXERCICIO');
  // O ano do calendário: no Pontual e no Passivo, o exercício seguinte (que o
  // Recorrente já monta desde julho) ainda tem pouco ou nada fechado.
  const [exercicio, setExercicio] = useState(() => new Date().getFullYear());
  const mudar = (parcial: Partial<FiltroDaVisaoGeral>): void =>
    setFiltro((f) => ({ ...f, ...parcial }));

  const periodoValido = /^\d{4}-\d{2}$/.test(filtro.de) && /^\d{4}-\d{2}$/.test(filtro.ate);
  const consulta = useQuery({
    queryKey: ['visao-geral', filtro],
    queryFn: () => buscarVisaoGeral(filtro),
    enabled: periodoValido && filtro.de <= filtro.ate && filtro.servicos.length > 0,
    // Trocar um filtro mantém os números anteriores na tela até chegarem os novos.
    placeholderData: keepPreviousData,
  });

  const dados = consulta.data;

  return (
    <div className="flex flex-col gap-6">
      <Cartao
        como="section"
        titulo="Recorte"
        subtitulo="Os três serviços, filtrados por período, etapa e quem participa."
        acao={
          <Botao
            variante="secundario"
            tamanho="pequeno"
            onClick={() => {
              setFiltro(filtroInicial());
              setAtalho('EXERCICIO');
              setExercicio(new Date().getFullYear());
            }}
          >
            Limpar filtros
          </Botao>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <CampoDeSelecao
            rotulo="Período"
            value={atalho}
            onChange={(e) => {
              const escolhido = e.target.value as Atalho;
              setAtalho(escolhido);
              if (escolhido === 'EXERCICIO') mudar(periodoDoExercicio(exercicio));
              else if (escolhido !== 'PERSONALIZADO') mudar(periodoDoAtalho(escolhido));
            }}
            opcoes={(Object.keys(ROTULO_DO_ATALHO) as Atalho[]).map((a) => ({
              valor: a,
              rotulo: ROTULO_DO_ATALHO[a],
            }))}
          />
          {atalho === 'EXERCICIO' ? (
            <CampoDeSelecao
              rotulo="Ano"
              value={String(exercicio)}
              onChange={(e) => {
                const ano = Number(e.target.value);
                setExercicio(ano);
                mudar(periodoDoExercicio(ano));
              }}
              opcoes={anosDoExercicio(vigente).map((ano) => ({
                valor: String(ano),
                rotulo: String(ano),
              }))}
            />
          ) : (
            <>
              <Campo
                rotulo="De"
                type="month"
                value={filtro.de}
                max={filtro.ate}
                onChange={(e) => {
                  setAtalho('PERSONALIZADO');
                  mudar({ de: e.target.value });
                }}
              />
              <Campo
                rotulo="Até"
                type="month"
                value={filtro.ate}
                min={filtro.de}
                onChange={(e) => {
                  setAtalho('PERSONALIZADO');
                  mudar({ ate: e.target.value });
                }}
              />
            </>
          )}
          <FiltroDeServicoEEtapa filtro={filtro} aoMudar={mudar} />
          <CampoDeSelecao
            rotulo="Cliente"
            value={filtro.clienteId}
            onChange={(e) => mudar({ clienteId: e.target.value })}
            opcoes={[
              { valor: '', rotulo: 'Todos os clientes' },
              ...(dados?.filtros.clientes ?? []).map((c) => ({
                valor: c.businessId,
                rotulo: c.razaoSocial,
              })),
            ]}
          />
          <CampoDeSelecao
            rotulo="Fornecedor"
            value={filtro.fornecedorId}
            onChange={(e) => mudar({ fornecedorId: e.target.value })}
            opcoes={[
              { valor: '', rotulo: 'Todos os fornecedores' },
              ...(dados?.filtros.fornecedores ?? []).map((c) => ({
                valor: c.businessId,
                rotulo: c.razaoSocial,
              })),
            ]}
          />
          <CampoDeSelecao
            rotulo="Assessor"
            value={filtro.assessorId}
            onChange={(e) => mudar({ assessorId: e.target.value })}
            opcoes={[
              { valor: '', rotulo: 'Todos os assessores' },
              ...(dados?.filtros.assessores ?? []).map((c) => ({
                valor: c.businessId,
                rotulo: c.razaoSocial,
              })),
            ]}
          />
          <CampoDeSelecao
            rotulo="Tipo de crédito"
            value={filtro.tipoCredito}
            onChange={(e) => mudar({ tipoCredito: e.target.value })}
            opcoes={[
              { valor: '', rotulo: 'Todos os tipos' },
              ...(dados?.filtros.tiposDeCredito ?? []).map((t) => ({
                valor: t,
                rotulo: ROTULO_DO_TIPO[t] ?? t,
              })),
            ]}
          />
        </div>
      </Cartao>

      {filtro.servicos.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum serviço escolhido"
          descricao="Marque ao menos um serviço no filtro para ver os números."
        />
      ) : !periodoValido || filtro.de > filtro.ate ? (
        <Aviso tom="atencao" titulo="Período incompleto">
          Escolha o mês de início e o de fim — o início antes do fim.
        </Aviso>
      ) : consulta.isError ? (
        <ErroDaApi titulo="Não foi possível montar a visão geral" erro={consulta.error} />
      ) : dados === undefined ? (
        <EstadoCarregando variante="painel" contexto="a visão geral" />
      ) : (
        <Conteudo dados={dados} servicos={filtro.servicos} atualizando={consulta.isFetching} />
      )}
    </div>
  );
}

function FiltroDeServicoEEtapa(props: {
  readonly filtro: FiltroDaVisaoGeral;
  readonly aoMudar: (parcial: Partial<FiltroDaVisaoGeral>) => void;
}): JSX.Element {
  const { filtro, aoMudar } = props;
  const alternar = <T,>(lista: readonly T[], item: T): T[] =>
    lista.includes(item) ? lista.filter((i) => i !== item) : [...lista, item];

  const marcados: FiltroMarcado[] = [
    ...(filtro.servicos.length === SERVICOS.length
      ? []
      : filtro.servicos.map((s) => ({
          rotulo: ROTULO_DO_SERVICO[s],
          tirar: () => aoMudar({ servicos: filtro.servicos.filter((x) => x !== s) }),
        }))),
    ...filtro.etapas.map((e) => ({
      rotulo: ROTULO_DA_ETAPA[e],
      tirar: () => aoMudar({ etapas: filtro.etapas.filter((x) => x !== e) }),
    })),
  ];

  return (
    <CampoDeFiltro
      rotulo="Serviços e etapas"
      marcados={marcados}
      semFiltro="Todos os serviços e etapas"
      aoLimpar={() => aoMudar({ servicos: [...SERVICOS], etapas: [] })}
    >
      <GrupoDeFiltro titulo="Serviço">
        {SERVICOS.map((s) => (
          <OpcaoDeFiltro
            key={s}
            rotulo={ROTULO_DO_SERVICO[s]}
            marcada={filtro.servicos.includes(s)}
            aoMudar={() => aoMudar({ servicos: alternar(filtro.servicos, s) })}
          />
        ))}
      </GrupoDeFiltro>
      <GrupoDeFiltro
        titulo="Etapa"
        nota="Sem nenhuma marcada, entram todas — inclusive simulações. O funil abaixo mostra todas as etapas sempre."
      >
        {ETAPAS.map((e) => (
          <OpcaoDeFiltro
            key={e}
            rotulo={`${ROTULO_DA_ETAPA[e]} — ${AJUDA_DA_ETAPA[e]}`}
            marcada={filtro.etapas.includes(e)}
            aoMudar={() => aoMudar({ etapas: alternar(filtro.etapas, e) })}
          />
        ))}
      </GrupoDeFiltro>
    </CampoDeFiltro>
  );
}

function Conteudo(props: {
  readonly dados: VisaoGeral;
  readonly servicos: readonly Servico[];
  readonly atualizando: boolean;
}): JSX.Element {
  const { dados } = props;
  const navegar = useNavigate();
  // A ordem dos serviços é fixa; o filtro só tira, nunca reordena (nem recolore).
  const servicos = SERVICOS.filter((s) => props.servicos.includes(s));
  const t = dados.totais;
  const r = dados.recebimentos;

  return (
    <div
      className={`flex flex-col gap-6 transition-opacity ${props.atualizando ? 'opacity-70' : ''}`}
      aria-busy={props.atualizando}
    >
      {dados.avisos.map((aviso) => (
        <Aviso key={aviso} tom="informacao" titulo="Sobre este recorte">
          {aviso}
        </Aviso>
      ))}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <CartaoIndicador
          rotulo="Créditos negociados"
          valor={<ValorFiscal valor={t.creditoNegociado} tamanho="grande" animado />}
          detalhe={`${t.operacoes} operaç${t.operacoes === 1 ? 'ão' : 'ões'}`}
        />
        <CartaoIndicador
          rotulo="Benefício econômico"
          valor={
            <ValorFiscal
              valor={t.beneficioEconomico}
              variante="economia"
              tamanho="grande"
              animado
            />
          }
          detalhe="O que os clientes deixaram de pagar"
        />
        <CartaoIndicador
          rotulo="Pago pelos clientes"
          valor={<ValorFiscal valor={t.valorDosClientes} tamanho="grande" animado />}
          detalhe="Pelo crédito ou pela dívida, no total das operações"
        />
        <CartaoIndicador
          rotulo="Clientes atendidos"
          valor={<span className="valor-fiscal text-3xl font-semibold">{t.clientes}</span>}
          detalhe="Cada empresa uma vez, em qualquer serviço"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <CartaoIndicador
          rotulo="Recebido"
          valor={<ValorFiscal valor={r.recebido} tamanho="grande" />}
          detalhe="Parcelas pagas no período"
        />
        <CartaoIndicador
          rotulo="A receber"
          valor={<ValorFiscal valor={r.aReceber} tamanho="grande" />}
          detalhe="Parcelas que vencem no período, ainda no prazo"
        />
        <CartaoIndicador
          rotulo="Em atraso"
          valor={
            <ValorFiscal
              valor={r.emAtraso}
              variante={r.emAtraso > 0 ? 'debito' : 'padrao'}
              tamanho="grande"
            />
          }
          detalhe="Venceram e não foram pagas"
        />
        <CartaoIndicador
          rotulo="Spread do Pontual"
          valor={<ValorFiscal valor={t.spread} tamanho="grande" />}
          detalhe="Venda menos compra, nas negociações do recorte"
        />
      </div>

      <PorServico dados={dados} servicos={servicos} />
      <GraficoMensal dados={dados} servicos={servicos} />
      <Funil dados={dados} servicos={servicos} />
      {dados.recorrente !== null && <DetalheDoRecorrente dados={dados.recorrente} />}

      <div className="grid gap-4 lg:grid-cols-2">
        <ListaDeRanking
          titulo="Maiores clientes"
          subtitulo="Por créditos negociados, somando os serviços."
          itens={dados.porCliente}
        />
        <ListaDeRanking
          titulo="Por fornecedor"
          subtitulo="De quem o crédito foi comprado."
          itens={dados.porFornecedor}
        />
        <ListaDeRanking
          titulo="Por assessor"
          subtitulo="Quem trouxe o volume. No Passivo, o assessor responsável pelo cliente."
          itens={dados.porAssessor}
        />
        {servicos.includes('PONTUAL') && (
          <ListaDeRanking
            titulo="Por tipo de crédito"
            subtitulo="Só o Pontual registra o tipo."
            itens={dados.porTipoCredito.map((i) => ({
              ...i,
              nome: ROTULO_DO_TIPO[i.nome] ?? i.nome,
            }))}
          />
        )}
      </div>

      <section className="flex min-w-0 flex-col gap-3">
        <h2 className="font-display text-lg font-semibold tracking-tight">Operações</h2>
        {dados.operacoes.length === 0 ? (
          <EstadoVazio
            titulo="Nenhuma operação neste recorte"
            descricao="Tente outro período, ou marque mais etapas no filtro."
          />
        ) : (
          <Tabela
            legenda="Operações do recorte, das maiores para as menores"
            densidade="compacta"
            colunas={COLUNAS_DAS_OPERACOES}
            dados={dados.operacoes}
            chaveDaLinha={(o) => `${o.servico}:${o.operacaoId}`}
            chaveDeCabecalhoDaLinha="cliente"
            aoClicarNaLinha={(o) => navegar(`/clientes/${o.clienteId}`)}
            alturaMaxima="36rem"
          />
        )}
        {dados.operacoesOmitidas > 0 && (
          <p className="text-xs text-conteudo-sutil">
            Mostrando as {dados.operacoes.length} maiores. Outras {dados.operacoesOmitidas} entram
            nas somas acima — use os filtros para chegar a elas.
          </p>
        )}
      </section>
    </div>
  );
}

function MarcaDoServico(props: { readonly servico: Servico }): JSX.Element {
  return (
    <span className="flex items-center gap-2">
      <span
        aria-hidden
        className={`inline-block h-2.5 w-2.5 shrink-0 rounded-sm ${COR_DO_SERVICO[props.servico]}`}
      />
      {ROTULO_DO_SERVICO[props.servico]}
    </span>
  );
}

function PorServico(props: {
  readonly dados: VisaoGeral;
  readonly servicos: readonly Servico[];
}): JSX.Element {
  type Linha = VisaoGeral['porServico'][number];
  const linhas = props.servicos
    .map((s) => props.dados.porServico.find((l) => l.servico === s))
    .filter((l): l is Linha => l !== undefined);
  const dinheiro = (chave: keyof Linha, titulo: string, economia = false): ColunaTabela<Linha> => ({
    chave,
    titulo,
    numerica: true,
    larguraMinima: '9rem',
    celula: (l) => (
      <ValorFiscal
        valor={l[chave] as number}
        tamanho="pequeno"
        {...(economia ? { variante: 'economia' as const } : {})}
      />
    ),
  });
  const colunas: readonly ColunaTabela<Linha>[] = [
    {
      chave: 'servico',
      titulo: 'Serviço',
      larguraMinima: '8rem',
      celula: (l) => <MarcaDoServico servico={l.servico} />,
    },
    { chave: 'operacoes', titulo: 'Operações', numerica: true, celula: (l) => l.operacoes },
    { chave: 'clientes', titulo: 'Clientes', numerica: true, celula: (l) => l.clientes },
    dinheiro('creditoNegociado', 'Créditos negociados'),
    dinheiro('beneficioEconomico', 'Benefício', true),
    dinheiro('valorDosClientes', 'Pago pelos clientes'),
    {
      chave: 'recebido',
      titulo: 'Recebido',
      numerica: true,
      larguraMinima: '8rem',
      celula: (l) =>
        l.servico === 'RECORRENTE' ? (
          <span className="text-conteudo-sutil" title="O Recorrente não registra pagamentos">
            —
          </span>
        ) : (
          <ValorFiscal valor={l.recebido} tamanho="pequeno" />
        ),
    },
    {
      chave: 'aReceber',
      titulo: 'A receber',
      numerica: true,
      larguraMinima: '8rem',
      celula: (l) =>
        l.servico === 'RECORRENTE' ? (
          <span className="text-conteudo-sutil">—</span>
        ) : (
          <ValorFiscal valor={l.aReceber} tamanho="pequeno" />
        ),
    },
    {
      chave: 'emAtraso',
      titulo: 'Em atraso',
      numerica: true,
      larguraMinima: '8rem',
      celula: (l) =>
        l.servico === 'RECORRENTE' ? (
          <span className="text-conteudo-sutil">—</span>
        ) : (
          <ValorFiscal
            valor={l.emAtraso}
            tamanho="pequeno"
            {...(l.emAtraso > 0 ? { variante: 'debito' as const } : {})}
          />
        ),
    },
  ];

  return (
    <section className="flex min-w-0 flex-col gap-3">
      <h2 className="font-display text-lg font-semibold tracking-tight">Por serviço</h2>
      <Tabela
        legenda="Os números de cada serviço no recorte"
        densidade="compacta"
        colunas={colunas}
        dados={linhas}
        chaveDaLinha={(l) => l.servico}
        chaveDeCabecalhoDaLinha="servico"
      />
    </section>
  );
}

/**
 * Créditos negociados mês a mês, empilhados por serviço.
 *
 * Uma cor por serviço, sempre a mesma (`--deca-serie-*`, validadas para
 * daltonismo nos dois temas); a legenda nomeia cada uma e o detalhe ao passar
 * o mouse dá os valores — a cor nunca é a única pista. Períodos longos
 * (mais de dois anos) viram barras por ano, para não virar um pente.
 */
function GraficoMensal(props: {
  readonly dados: VisaoGeral;
  readonly servicos: readonly Servico[];
}): JSX.Element {
  const [realce, setRealce] = useState<string | null>(null);

  const colunas = useMemo(() => {
    const meses = [...new Set(props.dados.porMes.map((m) => m.mes))].sort();
    const porAno = meses.length > 24;
    const chaves = porAno ? [...new Set(meses.map((m) => m.slice(0, 4)))] : meses;
    return chaves.map((chave) => {
      const doGrupo = props.dados.porMes.filter((m) =>
        porAno ? m.mes.startsWith(chave) : m.mes === chave,
      );
      const valores = props.servicos.map((servico) => ({
        servico,
        credito: doGrupo
          .filter((m) => m.servico === servico)
          .reduce((t, m) => t + m.creditoNegociado, 0),
      }));
      const [ano, mes] = chave.split('-');
      return {
        chave,
        rotulo: porAno
          ? chave
          : `${MESES[Number(mes) - 1]}${mes === '01' ? ` ${ano?.slice(2)}` : ''}`,
        rotuloLongo: porAno ? chave : `${MESES[Number(mes) - 1]}/${ano}`,
        valores,
        total: valores.reduce((t, v) => t + v.credito, 0),
      };
    });
  }, [props.dados.porMes, props.servicos]);

  const pico = Math.max(...colunas.map((c) => c.total), 1);
  const ALTURA = 160;
  const vazio = colunas.every((c) => c.total === 0);

  return (
    <Cartao
      como="section"
      titulo="Créditos negociados no tempo"
      subtitulo={
        colunas.length > 0 && colunas[0]!.chave.length === 4
          ? 'Por ano — o período passa de dois anos.'
          : 'Mês a mês, somando os serviços do recorte.'
      }
    >
      {vazio ? (
        <p className="text-sm text-conteudo-sutil">Nada negociado neste recorte.</p>
      ) : (
        <>
          {props.servicos.length > 1 && (
            <ul className="mb-4 flex flex-wrap gap-4 text-xs text-conteudo-secundario">
              {props.servicos.map((s) => (
                <li key={s}>
                  <MarcaDoServico servico={s} />
                </li>
              ))}
            </ul>
          )}
          <div
            className="flex items-end gap-1.5 overflow-x-auto pb-1 pt-16"
            role="img"
            aria-label={`Créditos negociados: ${colunas
              .map((c) => `${c.rotuloLongo} ${reais(c.total)}`)
              .join('; ')}`}
            onMouseLeave={() => setRealce(null)}
          >
            {colunas.map((c) => (
              <div
                key={c.chave}
                className="relative flex min-w-[2.4rem] flex-1 cursor-default flex-col items-center gap-2"
                onMouseEnter={() => setRealce(c.chave)}
                onFocus={() => setRealce(c.chave)}
                onBlur={() => setRealce(null)}
                tabIndex={0}
              >
                {realce === c.chave && <DetalheDaColuna coluna={c} />}
                {/* Segmentos de baixo para cima, na ordem fixa dos serviços; 2px de respiro entre eles. */}
                <div
                  className={`flex w-full flex-col-reverse gap-[2px] transition-opacity ${
                    realce !== null && realce !== c.chave ? 'opacity-50' : ''
                  }`}
                  style={{ height: `${ALTURA}px` }}
                >
                  {c.valores.map((v, i) => {
                    if (v.credito <= 0) return null;
                    const ultimo = c.valores.slice(i + 1).every((o) => o.credito <= 0);
                    return (
                      <div
                        key={v.servico}
                        className={`w-full ${COR_DO_SERVICO[v.servico]} ${ultimo ? 'rounded-t' : ''}`}
                        style={{ height: `${Math.max(2, (v.credito / pico) * ALTURA)}px` }}
                      />
                    );
                  })}
                </div>
                <span className="text-[0.65rem] uppercase text-conteudo-sutil">{c.rotulo}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Cartao>
  );
}

function DetalheDaColuna(props: {
  readonly coluna: {
    readonly rotuloLongo: string;
    readonly total: number;
    readonly valores: readonly { readonly servico: Servico; readonly credito: number }[];
  };
}): JSX.Element {
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute bottom-full z-10 mb-2 w-56 rounded-card border border-borda bg-superficie-elevada p-3 text-xs shadow-elevada"
    >
      <p className="mb-2 font-medium text-conteudo-primario">{props.coluna.rotuloLongo}</p>
      <ul className="flex flex-col gap-1">
        {props.coluna.valores.map((v) => (
          <li
            key={v.servico}
            className="flex items-center justify-between gap-3 text-conteudo-secundario"
          >
            <MarcaDoServico servico={v.servico} />
            <span className="valor-fiscal text-conteudo-primario">{reais(v.credito)}</span>
          </li>
        ))}
      </ul>
      {props.coluna.valores.length > 1 && (
        <p className="mt-2 flex justify-between border-t border-borda pt-2 text-conteudo-primario">
          <span>Total</span>
          <span className="valor-fiscal">{reais(props.coluna.total)}</span>
        </p>
      )}
    </div>
  );
}

/** O funil: quantas operações em cada etapa, por serviço — sem o filtro de etapa. */
function Funil(props: {
  readonly dados: VisaoGeral;
  readonly servicos: readonly Servico[];
}): JSX.Element {
  const etapas = ETAPAS.filter((e) => props.dados.porEtapa.some((p) => p.etapa === e));
  return (
    <Cartao
      como="section"
      titulo="Funil por etapa"
      subtitulo="Todas as etapas do período, mesmo as fora do filtro — o que ainda pode fechar e o que se perdeu."
    >
      {etapas.length === 0 ? (
        <p className="text-sm text-conteudo-sutil">Nenhuma operação no período.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] text-left text-sm">
            <caption className="sr-only">Operações por etapa e serviço</caption>
            <thead>
              <tr className="text-xs uppercase tracking-wide text-conteudo-sutil">
                <th scope="col" className="py-2 pr-4 font-medium">
                  Etapa
                </th>
                {props.servicos.map((s) => (
                  <th key={s} scope="col" className="py-2 pr-4 text-right font-medium">
                    <span className="inline-flex justify-end">
                      <MarcaDoServico servico={s} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {etapas.map((etapa) => (
                <tr key={etapa} className="border-t border-borda">
                  <th scope="row" className="py-3 pr-4 align-top font-normal">
                    <span className="flex flex-col">
                      <span className="text-conteudo-primario">{ROTULO_DA_ETAPA[etapa]}</span>
                      <span className="text-xs text-conteudo-sutil">{AJUDA_DA_ETAPA[etapa]}</span>
                    </span>
                  </th>
                  {props.servicos.map((s) => {
                    const celula = props.dados.porEtapa.find(
                      (p) => p.etapa === etapa && p.servico === s,
                    );
                    return (
                      <td key={s} className="py-3 pr-4 text-right align-top">
                        {celula === undefined ? (
                          <span className="text-conteudo-sutil">—</span>
                        ) : (
                          <span className="flex flex-col items-end">
                            <span className="text-conteudo-primario">
                              {celula.operacoes} operaç{celula.operacoes === 1 ? 'ão' : 'ões'}
                            </span>
                            <ValorFiscal
                              valor={celula.creditoNegociado}
                              tamanho="pequeno"
                              variante="sutil"
                            />
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Cartao>
  );
}

function ListaDeRanking(props: {
  readonly titulo: string;
  readonly subtitulo: string;
  readonly itens: readonly Ranking[];
}): JSX.Element {
  return (
    <Cartao como="section" titulo={props.titulo} subtitulo={props.subtitulo}>
      {props.itens.length === 0 ? (
        <p className="text-sm text-conteudo-sutil">Nada neste recorte.</p>
      ) : (
        <ol className="flex flex-col divide-y divide-borda">
          {props.itens.map((item) => (
            <li
              key={item.id ?? 'sem'}
              className="flex flex-wrap items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm text-conteudo-primario">{item.nome}</span>
                <span className="text-xs text-conteudo-sutil">
                  {item.operacoes} operaç{item.operacoes === 1 ? 'ão' : 'ões'} · benefício{' '}
                  {reais(item.beneficioEconomico)}
                </span>
              </span>
              <ValorFiscal valor={item.creditoNegociado} tamanho="pequeno" />
            </li>
          ))}
        </ol>
      )}
    </Cartao>
  );
}

const COLUNAS_DAS_OPERACOES: readonly ColunaTabela<OperacaoResumo>[] = [
  {
    chave: 'servico',
    titulo: 'Serviço',
    larguraMinima: '8rem',
    celula: (o) => <MarcaDoServico servico={o.servico} />,
  },
  {
    chave: 'cliente',
    titulo: 'Cliente',
    larguraMinima: '16rem',
    celula: (o) => <span className="text-conteudo-primario">{o.clienteNome}</span>,
  },
  {
    chave: 'fornecedor',
    titulo: 'Fornecedor',
    larguraMinima: '12rem',
    celula: (o) => o.fornecedorNome ?? <span className="text-conteudo-sutil">—</span>,
  },
  {
    chave: 'etapa',
    titulo: 'Etapa',
    larguraMinima: '10rem',
    celula: (o) => (
      <span className="flex flex-col">
        <span>{ROTULO_DA_ETAPA[o.etapa]}</span>
        <span className="text-xs text-conteudo-sutil">
          {ROTULO_DA_SITUACAO[o.situacao] ?? o.situacao}
        </span>
      </span>
    ),
  },
  {
    chave: 'quando',
    titulo: 'Quando',
    larguraMinima: '7rem',
    celula: (o) => (
      <span className="valor-fiscal text-sm">
        {o.de === o.ate ? mesCurto(o.de) : `${mesCurto(o.de)} a ${mesCurto(o.ate)}`}
      </span>
    ),
  },
  {
    chave: 'credito',
    titulo: 'Créditos',
    numerica: true,
    larguraMinima: '9rem',
    celula: (o) => <ValorFiscal valor={o.creditoNegociado} tamanho="pequeno" />,
  },
  {
    chave: 'beneficio',
    titulo: 'Benefício',
    numerica: true,
    larguraMinima: '9rem',
    celula: (o) => (
      <ValorFiscal valor={o.beneficioEconomico} tamanho="pequeno" variante="economia" />
    ),
  },
  {
    chave: 'valor',
    titulo: 'Pago pelo cliente',
    numerica: true,
    larguraMinima: '9rem',
    celula: (o) => <ValorFiscal valor={o.valorDosClientes} tamanho="pequeno" />,
  },
];

function mesCurto(aaaamm: string): string {
  const [ano, mes] = aaaamm.split('-');
  return `${MESES[Number(mes) - 1]}/${ano}`;
}

function reais(valor: number): string {
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
